import type {
    DataGridCellAddress,
    DataGridCellChange,
    DataGridCellValue,
} from "../types";

/** A cell's current value as its editor reads it; `undefined` when it can't. */
export type DataGridCellReader = (
    address: DataGridCellAddress,
) => DataGridCellValue | undefined;

/**
 * Undo and redo over cell changes (never data copies). Each entry is one user
 * action. A value the table sends stays in flight until the data shows it;
 * nothing reads a value that never shows as a rejection, so a save that never
 * lands keeps its step. History clears when the data overwrites a cell whose
 * sent value had landed (an outside edit) or a sent cell's row is gone, since
 * the stored values may no longer describe the cells.
 */
export interface UndoStack {
    /** Records an action the table just emitted. */
    record: (changes: readonly DataGridCellChange[]) => void;
    /** Changes that revert the last action, or `null` when there is none. */
    undo: () => readonly DataGridCellChange[] | null;
    /** Changes that re-apply the last undone action. */
    redo: () => readonly DataGridCellChange[] | null;
    /** What `undo` or `redo` would return next, without taking the step. */
    peek: (direction: "undo" | "redo") => readonly DataGridCellChange[] | null;
    /** Call whenever `data` changes, with a reader over the new data. */
    onDataChange: (readValue: DataGridCellReader) => void;
}

type Entry = readonly DataGridCellChange[];

/**
 * What the table sent one cell: values still in flight, oldest first, and the
 * value the cell last showed (before the first edit until one lands).
 */
interface SentCell {
    address: DataGridCellAddress;
    pendingValues: DataGridCellValue[];
    shownValue: DataGridCellValue;
    hasLanded: boolean;
}

/** Undo steps a spreadsheet keeps; `useSpreadsheetHistory` defaults to it too. */
export const MAX_UNDO_ENTRIES = 50;

const invert = (changes: Entry) =>
    changes.map((change) => ({
        rowId: change.rowId,
        columnId: change.columnId,
        value: change.previousValue,
        previousValue: change.value,
    }));

const toCellKey = ({ rowId, columnId }: DataGridCellAddress) =>
    JSON.stringify([rowId, columnId]);

/**
 * Reads one sent cell against new data and records what landed. A value the
 * cell already showed means nothing new landed (a refetch, or an undo back to
 * it still in flight). A sent value lands its oldest in-flight copy, so later
 * sends stay in flight. `overwritten` means a landed value was replaced by
 * one the table didn't send.
 */
const readSentCell = (cell: SentCell, value: DataGridCellValue) => {
    if (value === cell.shownValue) {
        return "held";
    }
    const index = cell.pendingValues.indexOf(value);
    if (index !== -1) {
        cell.shownValue = value;
        cell.hasLanded = true;
        cell.pendingValues = cell.pendingValues.slice(index + 1);
        return "held";
    }
    return cell.hasLanded ? "overwritten" : "held";
};

export const createUndoStack = (): UndoStack => {
    let undoEntries: Entry[] = [];
    let redoEntries: Entry[] = [];
    let sentCells = new Map<string, SentCell>();

    const send = (changes: Entry) => {
        for (const change of changes) {
            const key = toCellKey(change);
            const cell = sentCells.get(key) ?? {
                address: { rowId: change.rowId, columnId: change.columnId },
                pendingValues: [],
                shownValue: change.previousValue,
                hasLanded: false,
            };
            cell.pendingValues = [...cell.pendingValues, change.value].slice(
                -MAX_UNDO_ENTRIES,
            );
            sentCells.set(key, cell);
        }
    };

    /** Stops tracking cells no kept action touches. */
    const prune = () => {
        const referencedKeys = new Set(
            [...undoEntries, ...redoEntries].flat().map(toCellKey),
        );
        for (const key of sentCells.keys()) {
            if (!referencedKeys.has(key)) {
                sentCells.delete(key);
            }
        }
    };

    const clear = () => {
        undoEntries = [];
        redoEntries = [];
        sentCells = new Map();
    };

    const move = (from: Entry[], to: Entry[]) => {
        const entry = from.pop();
        if (!entry) {
            return null;
        }
        const reverted = invert(entry);
        to.push(reverted);
        send(reverted);
        return reverted;
    };

    return {
        record: (changes) => {
            if (changes.length === 0) {
                return;
            }
            undoEntries.push(changes);
            if (undoEntries.length > MAX_UNDO_ENTRIES) {
                undoEntries.shift();
            }
            redoEntries = [];
            send(changes);
            prune();
        },
        undo: () => move(undoEntries, redoEntries),
        redo: () => move(redoEntries, undoEntries),
        peek: (direction) => {
            const entry = (direction === "undo" ? undoEntries : redoEntries).at(
                -1,
            );
            return entry ? invert(entry) : null;
        },
        onDataChange: (readValue) => {
            const isStale = [...sentCells.values()].some((cell) => {
                const value = readValue(cell.address);
                return (
                    value === undefined ||
                    readSentCell(cell, value) === "overwritten"
                );
            });
            if (isStale) {
                clear();
            }
        },
    };
};
